import type { GlobalCalWithoutNs } from '@calcom/embed-core';
import confetti from 'canvas-confetti';
import { Breadcrumb, Stepper } from 'components';
import { PROFILE_ROUTES, ROUTES } from 'configs';
import * as Ch from 'pages/Subscriptions/SubscriptionInner/styled';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMedia } from 'react-use';
import { getCalApiForNamespace } from 'utils/calEmbed';

import * as O from './StripeCheckoutOutcome.styled';

export type StripeCheckoutOutcomeProps = {
  variant: 'success' | 'failure';
  /** Stripe Checkout Session id (cs_…) - shown truncated when present */
  sessionId?: string | null;
  planLabel?: string;
  onDismiss: () => void;
  /** `onboarding` when return URL includes `checkout=onboarding` (one-time onboarding checkout). */
  checkoutFlow?: 'subscription' | 'onboarding' | 'invoice';
  hostedInvoiceUrl?: string | null;
  amountLabel?: string | null;
};

const truncateSessionId = (id: string, max = 24) => {
  if (id.length <= max) return id;
  return `${id.slice(0, 14)}…${id.slice(-8)}`;
};

/** Stable namespace for Cal embed script (popup modal on this page only). */
const CAL_ONBOARDING_EMBED_NAMESPACE = 'gumbOnboardingOutcome';

/** Dedupe burst across React 18 StrictMode double-mount (refs reset on remount). */
const checkoutConfettiBurstKeys = new Set<string>();

const StripeCheckoutOutcome = ({
  variant,
  sessionId,
  planLabel,
  onDismiss,
  checkoutFlow = 'subscription',
  hostedInvoiceUrl = null,
  amountLabel = null,
}: StripeCheckoutOutcomeProps) => {
  const { t } = useTranslation();
  const prefersReducedMotion =
    useMedia('(prefers-reduced-motion: reduce)') === true;
  const showSuccessConfetti = variant === 'success' && !prefersReducedMotion;

  /** Ref on the content wrapper so we can measure its real center in the viewport. */
  const contentWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showSuccessConfetti) return;
    const burstKey =
      checkoutFlow === 'invoice'
        ? `invoice:${hostedInvoiceUrl ?? 'no-url'}`
        : `${checkoutFlow}:${sessionId ?? 'nosession'}`;
    if (checkoutConfettiBurstKeys.has(burstKey)) return;
    checkoutConfettiBurstKeys.add(burstKey);

    const rect = contentWrapRef.current?.getBoundingClientRect();
    const W = window.innerWidth;
    const H = window.innerHeight;
    // Dynamic origin: horizontal center of the content card as a fraction of viewport width.
    const originX = rect ? (rect.left + rect.width / 2) / W : 0.5;
    // Launch from ~30% down the card (so particles fall into the card).
    const originY = rect ? rect.top / H + 0.08 : 0.3;

    const shared = {
      particleCount: 130,
      spread: 60,
      startVelocity: 45,
      ticks: 260,
      zIndex: 50,
      disableForReducedMotion: true,
      origin: { x: originX, y: originY },
    };
    // Left cannon - particles fan to the left.
    confetti({ ...shared, angle: 115 });
    // Right cannon - particles fan to the right.
    confetti({ ...shared, angle: 65 });
  }, [showSuccessConfetti, checkoutFlow, sessionId, hostedInvoiceUrl]);

  const isOnboarding = checkoutFlow === 'onboarding';
  const isInvoice = checkoutFlow === 'invoice';
  const onboardingCalLink =
    process.env.REACT_APP_CAL_ONBOARDING_CAL_LINK?.trim() ?? '';
  const showOnboardingCalCta =
    isOnboarding && variant === 'success' && Boolean(onboardingCalLink);

  const [onboardingScheduleClicked, setOnboardingScheduleClicked] =
    useState(false);

  useEffect(() => {
    setOnboardingScheduleClicked(false);
  }, [sessionId, checkoutFlow]);

  const calApiPromiseRef = useRef<Promise<GlobalCalWithoutNs> | null>(null);

  const openOnboardingCalModal = useCallback(async () => {
    if (!onboardingCalLink) return;
    if (!calApiPromiseRef.current) {
      calApiPromiseRef.current = getCalApiForNamespace({
        namespace: CAL_ONBOARDING_EMBED_NAMESPACE,
      });
    }
    const cal = await calApiPromiseRef.current;
    cal('modal', {
      calLink: onboardingCalLink,
      config: {
        duration: '30',
      },
    });
  }, [onboardingCalLink]);

  const handleOnboardingScheduleClick = useCallback(() => {
    setOnboardingScheduleClicked(true);
    void openOnboardingCalModal();
  }, [openOnboardingCalModal]);

  const settingsPath = `${ROUTES.PROFILE}/${PROFILE_ROUTES.SETTINGS}`;
  const subscriptionPath = `${ROUTES.PROFILE}/${PROFILE_ROUTES.SUBSCRIPTION}`;
  const changePlanPath = `${PROFILE_ROUTES.SUBSCRIPTION}/${PROFILE_ROUTES.CHANGE_PLAN}`;

  const breadcrumbPlan = isInvoice
    ? t('subscriptions.checkout.invoiceReturnBreadcrumb')
    : variant === 'success'
    ? t('subscriptions.checkout.stripeReturnBreadcrumbSuccess')
    : t('subscriptions.checkout.stripeReturnBreadcrumbFailure');

  const planDisplay =
    planLabel ||
    (isOnboarding
      ? t('subscriptions.checkout.onboardingStripeReturnPlanFallback')
      : t('subscriptions.checkout.stripeReturnPlanFallback'));

  return (
    <Ch.CheckoutBackdrop>
      <O.OutcomePageWrap ref={contentWrapRef}>
        <Breadcrumb
          items={[
            {
              label: t('changePlan.breadcrumbSubscription'),
              to: subscriptionPath,
            },
            { label: breadcrumbPlan },
          ]}
        />

        {!isOnboarding ? (
          <Stepper
            steps={[
              {
                label: t('subscriptions.checkout.stepPlanSelected'),
                status: 'done',
              },
              {
                label: t('subscriptions.checkout.stepOrderSummary'),
                status: 'done',
              },
              {
                label: t('subscriptions.checkout.stepConfirmation'),
                status: 'active',
              },
            ]}
          />
        ) : null}

        {variant === 'success' ? (
          <O.SuccessWrap>
            <O.SuccessCircle aria-hidden>🎉</O.SuccessCircle>
            <O.SuccessTitle>
              {isInvoice
                ? t('subscriptions.checkout.invoiceReturnSuccessTitle')
                : isOnboarding
                ? t('subscriptions.checkout.onboardingStripeReturnSuccessTitle')
                : t('subscriptions.checkout.stripeReturnSuccessTitle')}
            </O.SuccessTitle>
            <O.SuccessSub>
              {isInvoice
                ? t('subscriptions.checkout.invoiceReturnSuccessSub', {
                    plan: planDisplay,
                  })
                : isOnboarding
                ? t('subscriptions.checkout.onboardingStripeReturnSuccessSub', {
                    plan: planDisplay,
                  })
                : t('subscriptions.checkout.stripeReturnSuccessSub', {
                    plan: planDisplay,
                  })}
            </O.SuccessSub>
            <O.SuccessDetails>
              <O.SuccessDetailsHeading>
                {isInvoice
                  ? t(
                      'subscriptions.checkout.invoiceReturnSuccessDetailsHeading',
                    )
                  : isOnboarding
                  ? t(
                      'subscriptions.checkout.onboardingStripeReturnSuccessDetailsHeading',
                    )
                  : t(
                      'subscriptions.checkout.stripeReturnSuccessDetailsHeading',
                    )}
              </O.SuccessDetailsHeading>
              <O.SuccessList>
                <O.SuccessListItem>
                  <O.SuccessCheck>✓</O.SuccessCheck>
                  {isInvoice
                    ? t(
                        'subscriptions.checkout.invoiceReturnSuccessBulletPlan',
                        { plan: planDisplay },
                      )
                    : isOnboarding
                    ? t(
                        'subscriptions.checkout.onboardingStripeReturnSuccessBulletPlan',
                        { plan: planDisplay },
                      )
                    : t(
                        'subscriptions.checkout.stripeReturnSuccessBulletPlan',
                        { plan: planDisplay },
                      )}
                </O.SuccessListItem>
                {isInvoice ? (
                  <>
                    <O.SuccessListItem>
                      <O.SuccessCheck>✓</O.SuccessCheck>
                      {t(
                        'subscriptions.checkout.invoiceReturnSuccessBulletActive',
                      )}
                    </O.SuccessListItem>
                    <O.SuccessListItem>
                      <O.SuccessCheck>✓</O.SuccessCheck>
                      {t(
                        'subscriptions.checkout.invoiceReturnSuccessBulletInvoice',
                      )}
                    </O.SuccessListItem>
                    {amountLabel ? (
                      <O.SuccessListItem>
                        <O.SuccessCheck>✓</O.SuccessCheck>
                        {t('subscriptions.checkout.invoiceAmount', {
                          amount: amountLabel,
                        })}
                      </O.SuccessListItem>
                    ) : null}
                  </>
                ) : !isOnboarding ? (
                  <O.SuccessListItem>
                    <O.SuccessCheck>✓</O.SuccessCheck>
                    {t(
                      'subscriptions.checkout.stripeReturnSuccessBulletActive',
                    )}
                  </O.SuccessListItem>
                ) : (
                  <O.SuccessListItem>
                    <O.SuccessCheck>✓</O.SuccessCheck>
                    {t(
                      'subscriptions.checkout.onboardingStripeReturnSuccessBulletNext',
                    )}
                  </O.SuccessListItem>
                )}
                {!isInvoice ? (
                  <O.SuccessListItem>
                    <O.SuccessCheck>✓</O.SuccessCheck>
                    {isOnboarding
                      ? t(
                          'subscriptions.checkout.onboardingStripeReturnSuccessBulletEmail',
                        )
                      : t(
                          'subscriptions.checkout.stripeReturnSuccessBulletEmail',
                        )}
                  </O.SuccessListItem>
                ) : null}
                {/* <O.SuccessListItem>
                  <O.SuccessCheck>✓</O.SuccessCheck>
                  {isOnboarding
                    ? t(
                        'subscriptions.checkout.onboardingStripeReturnSuccessBulletGuarantee',
                      )
                    : t(
                        'subscriptions.checkout.stripeReturnSuccessBulletGuarantee',
                      )}
                </O.SuccessListItem> */}
                {/* {sessionId ? (
                  <O.SuccessListItem>
                    <O.SuccessCheck aria-hidden> </O.SuccessCheck>
                    <span style={{ fontSize: 12, color: '#94a3b8' }}>
                      {t('subscriptions.checkout.stripeReturnSessionRef', {
                        id: truncateSessionId(sessionId),
                      })}
                    </span>
                  </O.SuccessListItem>
                ) : null} */}
              </O.SuccessList>
            </O.SuccessDetails>
            {showOnboardingCalCta ? (
              <O.OnboardingScheduleWrap>
                <O.BtnOnboardingScheduleCta
                  type="button"
                  $pulsing={!onboardingScheduleClicked}
                  onClick={handleOnboardingScheduleClick}
                >
                  {t(
                    'subscriptions.checkout.onboardingStripeReturnBookSession',
                  )}
                </O.BtnOnboardingScheduleCta>
              </O.OnboardingScheduleWrap>
            ) : null}
            {isInvoice ? (
              <O.SuccessBtns>
                {hostedInvoiceUrl ? (
                  <O.BtnPrimaryLink
                    as="a"
                    href={hostedInvoiceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('subscriptions.checkout.invoiceReturnSeeInvoice')}
                  </O.BtnPrimaryLink>
                ) : null}
                <O.BtnSecondaryOutcome type="button" onClick={onDismiss}>
                  {t('subscriptions.checkout.invoiceReturnGoSubscription')}
                </O.BtnSecondaryOutcome>
              </O.SuccessBtns>
            ) : !showOnboardingCalCta || onboardingScheduleClicked ? (
              <O.SuccessBtns>
                <O.BtnPrimaryLink to={ROUTES.HOME_PAGE} replace>
                  {t('subscriptions.checkout.stripeReturnGoHome')}
                </O.BtnPrimaryLink>
                <O.BtnSecondaryOutcome type="button" onClick={onDismiss}>
                  {t('subscriptions.checkout.stripeReturnViewSubscription')}
                </O.BtnSecondaryOutcome>
              </O.SuccessBtns>
            ) : null}
          </O.SuccessWrap>
        ) : (
          <O.SuccessWrap>
            <O.FailureCircle aria-hidden>✕</O.FailureCircle>
            <O.FailureTitle>
              {t('subscriptions.checkout.stripeReturnFailureTitle')}
            </O.FailureTitle>
            <O.FailureSub>
              {isOnboarding
                ? t('subscriptions.checkout.onboardingStripeReturnFailureSub')
                : t('subscriptions.checkout.stripeReturnFailureSub')}
            </O.FailureSub>
            <O.SuccessDetails>
              <O.SuccessDetailsHeading>
                {t('subscriptions.checkout.stripeReturnFailureDetailsHeading')}
              </O.SuccessDetailsHeading>
              <O.SuccessList>
                <O.SuccessListItem>
                  <O.SuccessCheck style={{ color: '#e24b4a' }}>
                    !
                  </O.SuccessCheck>
                  {t('subscriptions.checkout.stripeReturnFailureBullet1')}
                </O.SuccessListItem>
                <O.SuccessListItem>
                  <O.SuccessCheck style={{ color: '#e24b4a' }}>
                    !
                  </O.SuccessCheck>
                  {isOnboarding
                    ? t(
                        'subscriptions.checkout.onboardingStripeReturnFailureBullet2',
                      )
                    : t('subscriptions.checkout.stripeReturnFailureBullet2')}
                </O.SuccessListItem>
              </O.SuccessList>
            </O.SuccessDetails>
            <O.SuccessBtns>
              <O.BtnPrimaryLink to={changePlanPath} replace onClick={onDismiss}>
                {t('subscriptions.checkout.stripeReturnTryAgain')}
              </O.BtnPrimaryLink>
              <O.BtnSecondaryOutcome type="button" onClick={onDismiss}>
                {t('subscriptions.checkout.stripeReturnBackSubscription')}
              </O.BtnSecondaryOutcome>
            </O.SuccessBtns>
          </O.SuccessWrap>
        )}
      </O.OutcomePageWrap>
    </Ch.CheckoutBackdrop>
  );
};

export default StripeCheckoutOutcome;
